import {
  Avatar,
  AvatarFallback,
} from "@arcadeai/design-system/components/ui/atoms/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import type { ColumnDef } from "@tanstack/react-table";
import { Building2, Calendar, Users } from "lucide-react";
import { formatShortDate, getInitials } from "@/lib/utils";
import type { GroupedInvitation } from "../../types";
import { MyInvitationsActions } from "./my-invitations-actions";

export const getMyInvitationsColumns = (): ColumnDef<GroupedInvitation>[] => [
  {
    id: "project",
    accessorKey: "project_name",
    meta: {
      displayName: "Project",
      defaultVisible: true,
    },
    header: () => (
      <div className="flex items-center gap-1.5">
        <Building2 className="h-3.5 w-3.5" />
        Project
      </div>
    ),
    cell: ({ row }) => {
      const invitation = row.original;
      return (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <Building2 className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="truncate font-medium">
              {invitation.project_name}
            </div>
            <div className="truncate text-muted-foreground text-xs md:hidden">
              {invitation.org_name}
            </div>
          </div>
        </div>
      );
    },
  },
  {
    id: "organization",
    accessorKey: "org_name",
    meta: {
      displayName: "Organization",
      defaultVisible: true,
    },
    header: () => (
      <div className="flex items-center gap-1.5">
        <Building2 className="h-3.5 w-3.5" />
        Organization
      </div>
    ),
    cell: ({ row }) => (
      <span className="text-muted-foreground">{row.original.org_name}</span>
    ),
  },
  {
    id: "invited_by",
    accessorFn: (row) => row.inviters.at(0)?.name ?? "",
    meta: {
      displayName: "Invited by",
      defaultVisible: true,
    },
    header: () => (
      <div className="flex items-center gap-1.5">
        <Users className="h-3.5 w-3.5" />
        Invited by
      </div>
    ),
    cell: ({ row }) => {
      const invitation = row.original;
      const primaryInviter = invitation.inviters.at(0);
      const extraCount = invitation.inviters.length - 1;

      return (
        <div className="flex items-center gap-2">
          <div className="flex -space-x-1">
            {invitation.inviters.slice(0, 3).map((inviter) => (
              <Tooltip key={inviter.invitation_id}>
                <TooltipTrigger
                  render={
                    <Avatar className="h-7 w-7 border-2 border-background" />
                  }
                >
                  <AvatarFallback className="text-[9px]">
                    {getInitials(inviter.name)}
                  </AvatarFallback>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="text-xs">
                    <div className="font-medium">{inviter.name}</div>
                    <div className="text-muted-foreground">{inviter.email}</div>
                  </div>
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
          <span className="text-sm">
            {primaryInviter?.name}
            {extraCount > 0 && (
              <span className="text-muted-foreground"> +{extraCount}</span>
            )}
          </span>
        </div>
      );
    },
  },
  {
    id: "expires",
    accessorKey: "expires_at",
    meta: {
      displayName: "Expires",
      defaultVisible: true,
    },
    header: () => (
      <div className="flex items-center gap-1.5">
        <Calendar className="h-3.5 w-3.5" />
        Expires
      </div>
    ),
    cell: ({ row }) => (
      <span className="text-muted-foreground text-sm">
        {formatShortDate(row.original.expires_at)}
      </span>
    ),
    sortingFn: (rowA, rowB) => {
      const dateA = new Date(rowA.original.expires_at).getTime();
      const dateB = new Date(rowB.original.expires_at).getTime();
      return dateA - dateB;
    },
  },
  {
    id: "actions",
    meta: {
      displayName: "Actions",
      defaultVisible: true,
    },
    header: () => <div className="text-right">Actions</div>,
    cell: ({ row }) => <MyInvitationsActions invitation={row.original} />,
  },
];
